"use client";

import * as React from "react";
import { cn } from "@/src/lib/utils";
import type { StudentHit } from "./globalSearchApi";

export type PresenceLevel = "camera" | "room" | "unconfirmed" | "none";

export interface PresenceFlags {
  onCamera: boolean;
  inRoom: boolean;
  confirmed: boolean;
}

export function presenceLevel(flags: PresenceFlags | null | undefined): PresenceLevel {
  return flags?.onCamera ? "camera" : "none";
}

const DOT: Record<Exclude<PresenceLevel, "none">, string> = {
  camera: "bg-red-500",
  room: "bg-amber-500",
  unconfirmed: "bg-muted-foreground/60",
};

const Photo: React.FC<{ student: StudentHit }> = ({ student }) => {
  const [failed, setFailed] = React.useState(false);

  if (student.photo && !failed) {
    return (
      <img
        src={student.photo}
        alt=""
        className="h-8 w-8 shrink-0 rounded-full object-cover"
        onError={() => setFailed(true)}
      />
    );
  }

  const initials = student.name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");

  return (
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-medium text-muted-foreground">
      {initials || "—"}
    </span>
  );
};

export const PresenceAvatar: React.FC<{
  student: StudentHit;
  level: PresenceLevel;
}> = ({ student, level }) => {
  if (level === "none") return <Photo student={student} />;

  return (
    <span className="relative flex shrink-0">
      <Photo student={student} />
      <span className="absolute -right-0.5 -bottom-0.5 flex h-2.5 w-2.5 items-center justify-center">
        {level === "camera" && (
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
        )}
        <span
          className={cn("relative inline-flex h-2 w-2 rounded-full ring-2 ring-white", DOT[level])}
        />
      </span>
    </span>
  );
};

export function presenceLabelKey(level: PresenceLevel): string | null {
  return level === "camera" ? "label-global-search-live-camera" : null;
}

const PULSE_SIZE = {
  sm: { box: "h-1.5 w-1.5", dot: "h-1.5 w-1.5" },
  md: { box: "h-2 w-2", dot: "h-2 w-2" },
  lg: { box: "h-4 w-4", dot: "h-2.5 w-2.5" },
} as const;

export const LivePulse: React.FC<{ size?: keyof typeof PULSE_SIZE }> = ({ size = "md" }) => {
  const { box, dot } = PULSE_SIZE[size];

  return (
    <span className={cn("relative flex shrink-0 items-center justify-center", box)}>
      <span
        className={cn(
          "absolute inline-flex animate-ping rounded-full bg-red-400 opacity-75",
          dot
        )}
      />
      <span className={cn("relative inline-flex rounded-full bg-red-500", dot)} />
    </span>
  );
};
